import { useState, useEffect } from "react";
import { Users, UserCog, MapPin, DollarSign, TrendingUp, TrendingDown, Activity, Recycle, MessageSquare, Headset, Clock, ChevronRight } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getDashboardStats, getUsers, getWorkers, getPickups, getActivities } from "@/services/api";
import { ref, onValue, off } from "firebase/database";
import { rtdb } from "@/lib/firebase";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

export const Overview = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState([
    { title: "Total Users", value: "0", change: "0%", trend: "up", icon: Users, color: "bg-primary" },
    { title: "Active Captains", value: "0", change: "0%", trend: "up", icon: UserCog, color: "bg-secondary" },
    { title: "Pickups Today", value: "0", change: "0%", trend: "up", icon: MapPin, color: "bg-accent" },
    { title: "Revenue (Month)", value: "₹0", change: "0%", trend: "up", icon: DollarSign, color: "bg-primary-dark" },
  ]);
  const [appTickets, setAppTickets] = useState([]);
  const [manualTickets, setManualTickets] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  // Listen for tickets
  useEffect(() => {
    const ticketsRef = ref(rtdb, "tickets");
    const unsubscribe = onValue(ticketsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const ticketList = Object.keys(data).map(id => ({ id, ...data[id] }));
        
        const openApp = ticketList
          .filter(t => t.status === 'open' && t.source !== 'manual')
          .sort((a, b) => b.createdAt - a.createdAt)
          .slice(0, 5);
          
        const openManual = ticketList
          .filter(t => t.status === 'open' && t.source === 'manual')
          .sort((a, b) => b.createdAt - a.createdAt)
          .slice(0, 5);

        setAppTickets(openApp);
        setManualTickets(openManual);
      }
    });

    return () => off(ticketsRef);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, activitiesData] = await Promise.all([
          getDashboardStats(),
          getActivities()
        ]);

        if (statsData && statsData.success) {
          const { stats } = statsData;
          
          // Calculate subscription vs one-time revenue breakdown
          const subscriptionRevenue = stats.subscriptionRevenue || 0;
          const oneTimeRevenue = stats.oneTimeRevenue || 0;
          const totalRevenue = stats.monthlyRevenue || 0;
          
          setStats([
            {
              title: "Total Users",
              value: (stats.totalUsers || 0).toLocaleString(),
              change: "0%",
              trend: "up",
              icon: Users,
              color: "bg-primary",
            },
            {
              title: "Active Workers",
              value: (stats.activeCaptains || 0).toLocaleString(),
              change: "0%",
              trend: "up",
              icon: UserCog,
              color: "bg-secondary",
            },
            {
              title: "Total Pickups",
              value: (stats.totalPickups || 0).toLocaleString(),
              change: "0%",
              trend: "up",
              icon: MapPin,
              color: "bg-accent",
            },
            {
              title: "Revenue (Month)",
              value: `₹${totalRevenue.toLocaleString()}`,
              change: totalRevenue > 0 
                ? `${Math.round((subscriptionRevenue / totalRevenue) * 100)}% subs` 
                : "0%",
              trend: "up",
              icon: DollarSign,
              color: "bg-primary-dark",
            },
          ]);
        }

        if (activitiesData && activitiesData.success) {
          const activities = activitiesData.activities || [];
          // Format activities for display
          const formattedActivities = activities
            .sort((a, b) => new Date(b.timestamp || b.created_at) - new Date(a.timestamp || a.created_at))
            .slice(0, 5)
            .map((activity) => {
              const date = new Date(activity.timestamp || activity.created_at);
              const timeStr = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
              const dateStr = date.toLocaleDateString();

              return {
                id: activity.id,
                user: activity.admin_id === "unknown" ? "System" : (activity.admin_name || "Admin"),
                action: activity.description || activity.type,
                time: `${dateStr} ${timeStr}`,
                type: activity.type.includes("pickup")
                  ? "pickup"
                  : activity.type.includes("subscription")
                  ? "subscription"
                  : activity.type.includes("worker")
                  ? "registration"
                  : "default",
              };
            });
          setRecentActivity(formattedActivities);
        }
      } catch (error) {
        console.error("Error fetching overview data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <div>Loading...</div>;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Dashboard Overview</h1>
        <p className="text-muted-foreground mt-2">Welcome back! Here's what's happening with your waste management system.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.title} className="border-border hover:shadow-md transition-smooth">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                  <h3 className="text-2xl sm:text-3xl font-bold text-foreground mt-2">{stat.value}</h3>
                  <div className="flex items-center gap-1 mt-2">
                    {stat.trend === "up" ? (
                      <TrendingUp className="w-4 h-4 text-success" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-destructive" />
                    )}
                    <span className={`text-sm font-medium ${
                      stat.trend === "up" ? "text-success" : "text-destructive"
                    }`}>
                      {stat.change}
                    </span>
                    <span className="text-sm text-muted-foreground ml-1">vs last month</span>
                  </div>
                </div>
                <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center shadow-md`}>
                  <stat.icon className="w-6 h-6 text-primary-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tickets Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* App Customer Tickets */}
        <Card className="border-border overflow-hidden">
          <CardHeader className="border-b bg-muted/30">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <MessageSquare className="w-5 h-5 text-primary" />
                Open App Tickets
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate("/tickets")} className="text-xs">
                View All
              </Button>
            </div>
            <CardDescription>Recent support requests from mobile app</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {appTickets.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">No open app tickets</div>
              ) : (
                appTickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    onClick={() => navigate("/tickets")}
                    className="w-full text-left p-4 hover:bg-muted/50 transition-smooth flex items-center justify-between group"
                  >
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-semibold group-hover:text-primary transition-colors">{ticket.subject}</span>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Users className="w-3 h-3" />
                        <span>{ticket.userName}</span>
                        <span>•</span>
                        <Clock className="w-3 h-3" />
                        <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-smooth" />
                  </button>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Manual Complaints */}
        <Card className="border-border overflow-hidden border-orange-100">
          <CardHeader className="border-b bg-orange-50/50">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg text-orange-900">
                <Headset className="w-5 h-5 text-orange-500" />
                Open Manual Complaints
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate("/tickets")} className="text-xs text-orange-700 hover:text-orange-900 hover:bg-orange-100">
                View All
              </Button>
            </div>
            <CardDescription className="text-orange-700/70">Complaints recorded via support calls</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {manualTickets.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">No open manual complaints</div>
              ) : (
                manualTickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    onClick={() => navigate("/tickets")}
                    className="w-full text-left p-4 hover:bg-orange-50 transition-smooth flex items-center justify-between group"
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold group-hover:text-orange-700 transition-colors">{ticket.subject.replace('[MANUAL] ', '')}</span>
                        <Badge variant="default" className="text-[8px] h-3.5 px-1 bg-orange-500 hover:bg-orange-600 border-none animate-pulse">
                          MANUAL
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Users className="w-3 h-3" />
                        <span>{ticket.userName}</span>
                        <span>•</span>
                        <Clock className="w-3 h-3" />
                        <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-orange-500 opacity-0 group-hover:opacity-100 transition-smooth" />
                  </button>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Recent Activity
          </CardTitle>
          <CardDescription>Latest actions across the platform</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-smooth">
                <div className={`w-2 h-2 rounded-full ${
                  activity.type === "pickup" ? "bg-primary" :
                  activity.type === "subscription" ? "bg-secondary" :
                  activity.type === "completed" ? "bg-success" :
                  activity.type === "registration" ? "bg-accent" :
                  "bg-muted-foreground"
                }`} />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{activity.user}</p>
                  <p className="text-sm text-muted-foreground">{activity.action}</p>
                </div>
                <span className="text-xs text-muted-foreground">{activity.time}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Overview;
